/**
 * Work → Goals (/work/goals). The dated outcomes everything serves ("VSL
 * live and converting" by Oct 25). Each shows who owns it, the source it's
 * for and how far its tasks have got. ?goal=<id> opens one.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, Flag, Plus } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button, Skeleton, useToast } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Card, Empty, PageHeader } from "@/features/command/ui";
import { GoalCard } from "@/features/work/goals/GoalCard";
import { TaskDrawer, type TaskTarget } from "@/features/work/TaskDrawer";
import { PersonPills } from "@/features/work/fields";
import { DayPicker } from "@/features/work/DayPicker";
import { useLookups, useMe } from "@/features/work/lib";
import type { Goal } from "@/features/work/types";

export function GoalsPage() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const { people, sources, team, wsTz, reload: reloadLookups } = useLookups();
  const { me } = useMe();
  const [goals, setGoals] = useState<Goal[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(params.get("goal"));
  const [showClosed, setShowClosed] = useState(false);
  const [drawer, setDrawer] = useState<TaskTarget | null>(null);
  const [taskVersion, setTaskVersion] = useState(0);
  // Inline "add a goal"
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [owner, setOwner] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ goals: Goal[] }>("/command/goals");
      setGoals(d.goals);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load goals");
      setGoals((g) => g ?? []);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (!owner && me && people.some((p) => p.id === me.id)) setOwner(me.id); }, [me, people, owner]);
  useEffect(() => {
    const g = params.get("goal");
    if (g) { setOpenId(g); if (goals?.some((x) => x.id === g && x.status !== "active")) setShowClosed(true); }
  }, [params, goals]);

  const changed = () => { void load(); void reloadLookups(); };

  const toggle = (id: string) => {
    const next = openId === id ? null : id;
    setOpenId(next);
    const p = new URLSearchParams(params);
    if (next) p.set("goal", next); else p.delete("goal");
    setParams(p, { replace: true });
  };

  const add = async () => {
    const t = title.trim();
    if (!t) return;
    setAdding(true);
    try {
      const r = await api.post<{ goal: Goal }>("/command/goals", { title: t, due_date: due || null, owner_id: owner });
      setTitle(""); setDue("");
      toast.success("Goal added", due ? undefined : "Give it a date so it can be planned toward.");
      await load();
      void reloadLookups();
      setOpenId(r.goal.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add the goal");
    } finally {
      setAdding(false);
    }
  };

  const active = (goals ?? []).filter((g) => g.status === "active");
  const closed = (goals ?? []).filter((g) => g.status !== "active");
  const card = (g: Goal) => (
    <GoalCard
      key={g.id}
      goal={g}
      open={openId === g.id}
      onToggleOpen={() => toggle(g.id)}
      onChanged={changed}
      onOpenTask={(t) => setDrawer(t)}
      people={people}
      sources={sources}
      team={team}
      wsTz={wsTz}
      taskVersion={taskVersion}
    />
  );

  return (
    <div className="mx-auto max-w-5xl space-y-4 pb-12">
      <PageHeader
        eyebrow="Work"
        title="Goals"
        subtitle="The dated outcomes everything serves. Point tasks at a goal and watch it fill up."
      />

      <Card className="p-3 sm:p-4">
        <form onSubmit={(e) => { e.preventDefault(); void add(); }} className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 focus-within:border-indigo-400/50">
            <Flag className="h-4 w-4 shrink-0 text-violet-300" />
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder='Add a goal, like "VSL live and converting"'
              className="min-w-0 flex-1 bg-transparent py-1 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
              aria-label="New goal"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DayPicker value={due} onChange={setDue} placeholder="By when" className="w-44" />
            {people.length > 1 && <PersonPills people={people} value={owner} onChange={setOwner} />}
            <Button type="submit" loading={adding} disabled={!title.trim()} leftIcon={<Plus className="h-4 w-4" />}>Add goal</Button>
          </div>
        </form>
      </Card>

      {goals === null ? (
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
      ) : (
        <>
          {active.length === 0 ? (
            <Card>
              <Empty icon={Flag} title="No goals yet">
                A goal is an outcome with a date, like "Content engine running every day by Nov 8". Add one above, then point tasks at it.
              </Empty>
            </Card>
          ) : (
            <div className="space-y-3">{active.map(card)}</div>
          )}

          {closed.length > 0 && (
            <div className="space-y-3">
              <button type="button" onClick={() => setShowClosed((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-100">
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showClosed && "rotate-180")} />
                Reached and dropped ({closed.length})
              </button>
              {showClosed && <div className="space-y-3">{closed.map(card)}</div>}
            </div>
          )}
        </>
      )}

      <TaskDrawer target={drawer} onClose={() => setDrawer(null)} onChanged={() => { setTaskVersion((v) => v + 1); void load(); }} />
    </div>
  );
}
